import React, { FC, useState } from "react";

// Components
import { Card } from "@UI/Components/Card/Card";

// Hooks
import { FormattedMessage } from "@I18n/Components/FormattedMessage/FormattedMessage";
import messages from "./messages";
import { PaymentMethodCard } from "../PaymentMethodCard/PaymentMethodCard";
import { PaymentMethodType } from "@modules/PaymentProcessing/types";
import { BancontactPaymentCard } from "@modules/PaymentProcessing/modules/Bancontact/Components/BancontactPaymentCard/BancontactPaymentCard";
import { CreditCardPaymentCard } from "@modules/PaymentProcessing/modules/CreditCard/Components/CreditCardPaymentCard/CreditCardPaymentCard";
import { BankTransferPaymentCard } from "@modules/PaymentProcessing/modules/BankTransfer/Components/BankTransferPaymentCard/BankTransferPaymentCard";
import { PayPalPaymentCard } from "@modules/PaymentProcessing/modules/PayPal/Components/PayPalPaymentCard/PayPalPaymentCard";
import { useCurrentUser } from "@modules/Authentication/hooks/useCurrentUser";

interface Props {
  onSelect: (value: PaymentMethodType) => void;
  showCash?: boolean;
  enabledPaymentMethods?: PaymentMethodType[];
  allowedPaymentMethods?: PaymentMethodType[];
  showPayPalAsPaymentMethod?: boolean;
}

export const PaymentMethodSelect: FC<Props> = ({
  onSelect,
  showCash,
  enabledPaymentMethods,
  allowedPaymentMethods,
  showPayPalAsPaymentMethod,
}) => {
  const [noCashModalOpen, setNoCashModalOpen] = useState(false);

  const { currentUser } = useCurrentUser();

  const isUserFrench = currentUser?.countryId === 2;

  const handleToggleCashModal = () => {
    setNoCashModalOpen(!noCashModalOpen);
  };

  return (
    <Card
      background={{ base: "transparent", md: "white" }}
      padding={{ base: 0, md: 6 }}
      shadow={{ base: "none", md: "lg" }}
      overflow="visible"
      data-testid="choose-payment-method"
    >
      {allowedPaymentMethods.includes(PaymentMethodType.CREDIT_CARD) && (
        <CreditCardPaymentCard
          onClick={onSelect}
          disabled={
            !enabledPaymentMethods.includes(PaymentMethodType.CREDIT_CARD)
          }
          isUserFrench={isUserFrench}
        />
      )}

      {showPayPalAsPaymentMethod &&
        isUserFrench &&
        allowedPaymentMethods.includes(PaymentMethodType.PAYPAL) && (
          <PayPalPaymentCard
            onClick={(paymentMethod) =>
              onSelect(paymentMethod as PaymentMethodType)
            }
            disabled={!enabledPaymentMethods.includes(PaymentMethodType.PAYPAL)}
          />
        )}

      {allowedPaymentMethods.includes(PaymentMethodType.BANCONTACT) && (
        <BancontactPaymentCard
          onClick={onSelect}
          disabled={
            !enabledPaymentMethods.includes(PaymentMethodType.BANCONTACT)
          }
        />
      )}

      {allowedPaymentMethods.includes(PaymentMethodType.BANK_TRANSFER) && (
        <BankTransferPaymentCard
          onClick={onSelect}
          disabled={
            !enabledPaymentMethods.includes(PaymentMethodType.BANK_TRANSFER)
          }
        />
      )}
      {showCash && (
        <PaymentMethodCard
          data-testid="cash-payment-link"
          title={<FormattedMessage {...messages.cash} />}
          onClick={handleToggleCashModal}
          cardNetworkIcons={[]}
          value="cash"
          forbidden
          disabled
          disabledModalTitle={<FormattedMessage {...messages.noCash} />}
          disabledModalContent={
            <FormattedMessage
              {...messages.allTransactionsWithServiceProvidersHaveToBeProcessedByRingTwice}
            />
          }
        />
      )}
    </Card>
  );
};
